import React, { useEffect, useState } from 'react';
import { LoaderIcon, Upload, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Button } from 'components/ui/button';
import { GoogleReCaptcha } from 'react-google-recaptcha-v3';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { useUpscaleImageContext } from '../provider';
import { ImageGenService } from '../services/image-gen.api';
import toast from 'react-hot-toast';
import SaveImageModal from './modals/SaveImageModal';
import useIsMobile from 'hooks/useIsMobile';
export default function ImageGenerateForm() {
  const [selectedQuality, setSelectedQuality] = useState('medium');
  const [refreshReCaptcha, setRefreshReCaptcha] = useState(false);

  const [token, setToken] = useState('');
  const navigate = useNavigate();
  const { selectedImage, setSelectedImage, setGeneratedImage, generatedImage } =
    useUpscaleImageContext();

  const { mutate: upscaleImage, isPending } = useMutation({
    mutationFn: (formData: FormData) =>
      ImageGenService.upscaleImage(formData, token),
    onSuccess: (data) => {
      console.log(data);

      setGeneratedImage(data.data);

      toast.success('Image Upscaled Successfully');
      navigate('/upscale-image/result');
    },
  });

  const isMobile = useIsMobile();

  const onSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setRefreshReCaptcha((r) => !r);

    const formData = new FormData();
    if (selectedImage) {
      formData.append('file', selectedImage);
    }

    upscaleImage(formData);
  };

  const validateFile = (file: File) => {
    setSelectedImage(file);
    return file;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const file = e.target.files?.[0];
    if (file) {
      validateFile(file);
    }
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    navigate('/upscale-image/get-started');
  };

  useEffect(() => {
    if (!selectedImage) {
      navigate('/upscale-image/get-started');
    }
  }, [selectedImage]);

  return (
    <div className="bg-background-main text-text-primary flex flex-col rounded-md p-3 px-4 col-span-3 min-w-[300px]">
      <p className="text-text-secondary uppercase text-sm">IMAGE TYPE</p>

      <div className="mt-3 flex flex-1 flex-col gap-4">
        <div className="flex flex-col gap-4">
          {selectedImage ? (
            <button
              onClick={handleRemoveImage}
              className="flex text-sm items-center gap-2 justify-between border-2 border-dashed border-primaryAccent-5 text-primary text-[17px] rounded-md p-2 e"
            >
              <span>{selectedImage.name}</span>
              <X size={16} />
            </button>
          ) : (
            <button className="relative text-sm flex items-center justify-center gap-2 w-full border-2 border-dashed border-primaryAccent-5 text-primary text-[17px] rounded-md p-2 ">
              <label
                htmlFor="fileInput"
                className="absolute inset-0 cursor-pointer"
              >
                <input
                  id="fileInput"
                  type="file"
                  accept="image/png, image/jpeg, image/jpg"
                  className="hidden"
                  onChange={handleFileChange}
                />
              </label>
              <Upload size={16} /> Upload Image
            </button>
          )}

          <div>
            <label className="text-xs lg:text-sm text-text-secondary">
              QUALITY
            </label>
            <Select value={selectedQuality} onValueChange={setSelectedQuality}>
              <SelectTrigger className="mt-2">
                <SelectValue placeholder="Select Quality" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="w-full lg:block">
          <Button
            onClick={onSubmit}
            disabled={
              !selectedImage ||
              isPending ||
              window.location.pathname === '/upscale-image/result'
            }
            className="w-full gap-3 mt-2 text-sm disabled:cursor-not-allowed"
            variant={'customPrimary'}
          >
            {isPending && (
              <div className="animate-spin">
                <LoaderIcon size={20} />
              </div>
            )}
            {isPending ? 'Upscaling Image' : 'Upscale Image'}
          </Button>
        </div>
      </div>

      {isMobile && (
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-background-main border-t flex gap-4">
          <Button
            variant={'customSecondary'}
            size="sm"
            className="bg-secondary flex-1"
          >
            Export
          </Button>
          <SaveImageModal generatedImage={generatedImage} />
        </div>
      )}

      <GoogleReCaptcha
        action="KaiRecaptcha0911"
        onVerify={setToken}
        refreshReCaptcha={refreshReCaptcha}
      />
    </div>
  );
}
